export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown

  constructor(code: string, status: number, details?: unknown) {
    super(code)
    this.name = "ApiError"
    this.code = code
    this.status = status
    this.details = details
  }
}

type Options = { method?: string; body?: unknown }

/** 서버가 오류로 주는 모양(`server/src/middleware/error.ts`). 성공 응답은 라우트마다 다르다. */
type ErrorPayload = { code?: string; details?: unknown }

async function errorPayload(res: Response): Promise<ErrorPayload | null> {
  const contentType = res.headers.get("content-type") ?? ""
  if (!contentType.includes("application/json")) return null
  return (await res.json().catch(() => null)) as ErrorPayload | null
}

let onUnauthorized: (() => void) | null = null

/** 401 을 받으면 세션을 비우고 로그인 화면으로 보내도록 App 이 등록한다. */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method: options.method ?? "GET",
      credentials: "same-origin",
      headers: options.body === undefined ? {} : { "content-type": "application/json" },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch {
    throw new ApiError("NETWORK", 0)
  }

  if (res.status === 204) return undefined as T

  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.()
    const payload = await errorPayload(res)
    throw new ApiError(payload?.code ?? "INTERNAL", res.status, payload?.details)
  }

  const contentType = res.headers.get("content-type") ?? ""
  return (contentType.includes("application/json") ? await res.json() : null) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ?? {} }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
}

/**
 * 파일 내려받기. 샌드박스가 아닌 실제 브라우저이므로 링크를 만들어 눌러 준다.
 * 인증 쿠키가 실려야 하므로 fetch 로 받아 Blob 으로 저장한다.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(path, { credentials: "same-origin" })
  if (!res.ok) {
    const payload = await errorPayload(res)
    throw new ApiError(payload?.code ?? "INTERNAL", res.status)
  }

  const disposition = res.headers.get("content-disposition") ?? ""
  const match = /filename="([^"]+)"/.exec(disposition)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = match?.[1] ?? fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
